import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Save,
  Radio,
  Calendar,
  Megaphone,
  Loader2,
  ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-brand-ivory border border-brand-border rounded-md px-3 py-2 text-sm text-brand-deep placeholder:text-brand-quiet/60 focus:outline-none focus:border-brand-olive focus:ring-1 focus:ring-brand-olive transition-colors";

const labelCls =
  "text-[10px] font-semibold tracking-[0.16em] uppercase text-brand-quiet";

const cardCls =
  "border border-brand-border rounded-2xl bg-brand-ivory overflow-hidden";

const rowFrameCls =
  "border border-brand-border rounded-xl bg-brand-cream p-4 space-y-3";

const sectionHeader = ({ Icon, title, subtitle, action }) => (
  <div className="flex items-start justify-between gap-4 mb-5">
    <div className="flex items-start gap-3">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-linen text-brand-olive border border-brand-olive/30 flex-shrink-0">
        <Icon className="w-4 h-4" strokeWidth={1.7} />
      </span>
      <div>
        <h3 className="font-display text-lg text-brand-deep leading-tight">
          {title}
        </h3>
        <p className="mt-0.5 text-xs text-brand-quiet leading-relaxed max-w-md">
          {subtitle}
        </p>
      </div>
    </div>
    {action}
  </div>
);

/* -------------------- FEED ITEMS EDITOR -------------------- */

const emptyFeedItem = () => ({
  id: null,
  title: "",
  source_event: "",
  match_strength: 80,
  display_order: 999,
  active: true,
  _dirty: true,
  _new: true,
});

function FeedItemsEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/site/feed`, {
        withCredentials: true,
      });
      setItems(
        (res.data.items || []).map((it) => ({ ...it, _dirty: false, _new: false }))
      );
    } catch {
      toast.error("Couldn't load feed items");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (idx, patchObj) =>
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, ...patchObj, _dirty: true } : it))
    );

  const save = async (idx) => {
    const it = items[idx];
    if (!it.title.trim() || !it.source_event.trim()) {
      toast.error("Title and source event are required");
      return;
    }
    const payload = {
      title: it.title.trim(),
      source_event: it.source_event.trim(),
      match_strength: Math.max(0, Math.min(100, Number(it.match_strength) || 0)),
      display_order: Number(it.display_order) || 0,
      active: !!it.active,
    };
    setSavingId(it.id || `new-${idx}`);
    try {
      if (it._new) {
        const res = await axios.post(`${API}/admin/site/feed`, payload, {
          withCredentials: true,
        });
        setItems((prev) =>
          prev.map((row, i) =>
            i === idx ? { ...res.data, _dirty: false, _new: false } : row
          )
        );
        toast.success("Feed item added");
      } else {
        const res = await axios.put(
          `${API}/admin/site/feed/${it.id}`,
          payload,
          { withCredentials: true }
        );
        setItems((prev) =>
          prev.map((row, i) =>
            i === idx ? { ...res.data, _dirty: false, _new: false } : row
          )
        );
        toast.success("Feed item saved");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (idx) => {
    const it = items[idx];
    if (!window.confirm(`Delete "${it.title || "this item"}"?`)) return;
    if (it._new) {
      setItems((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    setDeletingId(it.id);
    try {
      await axios.delete(`${API}/admin/site/feed/${it.id}`, {
        withCredentials: true,
      });
      setItems((prev) => prev.filter((_, i) => i !== idx));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const addNew = () =>
    setItems((prev) => [
      ...prev,
      { ...emptyFeedItem(), display_order: (prev[prev.length - 1]?.display_order || 0) + 1 },
    ]);

  return (
    <div className={cardCls} data-testid="admin-feed-editor">
      <div className="px-6 pt-6 pb-2">
        {sectionHeader({
          Icon: Radio,
          title: "Hero Feed Items",
          subtitle:
            "Anonymized market signals shown on the homepage hero. Active items rotate in groups of 5 when there are more than five. Never include real company or lead names.",
          action: (
            <button
              type="button"
              onClick={addNew}
              data-testid="admin-feed-add"
              className="inline-flex items-center gap-1.5 bg-brand-olive hover:bg-brand-hover text-brand-cream px-3.5 py-2 rounded-full text-xs font-medium tracking-wide transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add item
            </button>
          ),
        })}
      </div>

      <div className="px-6 pb-6 space-y-3">
        {loading ? (
          <div className="py-10 text-center text-brand-quiet text-sm">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-brand-quiet text-sm">
            No feed items yet. Click "Add item" to create the first.
          </div>
        ) : (
          items.map((it, idx) => (
            <div
              key={it.id || `new-${idx}`}
              data-testid={`admin-feed-row-${idx}`}
              className={rowFrameCls}
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-5">
                  <label className={labelCls}>Title</label>
                  <input
                    value={it.title}
                    onChange={(e) => patch(idx, { title: e.target.value })}
                    className={inputCls}
                    placeholder="Functional Mushroom Extracts"
                    data-testid={`admin-feed-title-${idx}`}
                  />
                </div>
                <div className="md:col-span-4">
                  <label className={labelCls}>Source event</label>
                  <input
                    value={it.source_event}
                    onChange={(e) => patch(idx, { source_event: e.target.value })}
                    className={inputCls}
                    placeholder="Vitafoods Europe"
                    data-testid={`admin-feed-source-${idx}`}
                  />
                </div>
                <div className="md:col-span-3 grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelCls}>Match %</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={it.match_strength}
                      onChange={(e) =>
                        patch(idx, { match_strength: e.target.value })
                      }
                      className={inputCls}
                      data-testid={`admin-feed-match-${idx}`}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Order</label>
                    <input
                      type="number"
                      value={it.display_order}
                      onChange={(e) =>
                        patch(idx, { display_order: e.target.value })
                      }
                      className={inputCls}
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 pt-1">
                <label className="inline-flex items-center gap-2 text-xs text-brand-warm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!it.active}
                    onChange={(e) => patch(idx, { active: e.target.checked })}
                    className="accent-brand-olive"
                    data-testid={`admin-feed-active-${idx}`}
                  />
                  Active on site
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    disabled={deletingId === it.id}
                    className="inline-flex items-center gap-1.5 text-xs text-brand-muted hover:text-red-700 px-2.5 py-1.5 rounded-full border border-transparent hover:border-red-300 transition-colors disabled:opacity-50"
                    data-testid={`admin-feed-delete-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => save(idx)}
                    disabled={!it._dirty || savingId === (it.id || `new-${idx}`)}
                    className="inline-flex items-center gap-1.5 bg-brand-olive hover:bg-brand-hover disabled:opacity-40 text-brand-cream px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors"
                    data-testid={`admin-feed-save-${idx}`}
                  >
                    {savingId === (it.id || `new-${idx}`) ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Save className="w-3.5 h-3.5" />
                    )}
                    Save
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* -------------------- CONFERENCES EDITOR -------------------- */

const emptyConf = () => ({
  id: null,
  name: "",
  dates: "",
  location: "",
  url: "",
  display_order: 999,
  active: true,
  _dirty: true,
  _new: true,
});

function ConferencesEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/site/conferences`, {
        withCredentials: true,
      });
      setItems(
        (res.data.conferences || []).map((it) => ({
          ...it,
          url: it.url || "",
          location: it.location || "",
          _dirty: false,
          _new: false,
        }))
      );
    } catch {
      toast.error("Couldn't load conferences");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (idx, patchObj) =>
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, ...patchObj, _dirty: true } : it))
    );

  const save = async (idx) => {
    const it = items[idx];
    if (!it.name.trim() || !it.dates.trim()) {
      toast.error("Name and dates are required");
      return;
    }
    const payload = {
      name: it.name.trim(),
      dates: it.dates.trim(),
      location: it.location.trim(),
      url: it.url.trim() || null,
      display_order: Number(it.display_order) || 0,
      active: !!it.active,
    };
    setSavingId(it.id || `new-${idx}`);
    try {
      if (it._new) {
        const res = await axios.post(`${API}/admin/site/conferences`, payload, {
          withCredentials: true,
        });
        setItems((prev) =>
          prev.map((row, i) =>
            i === idx
              ? { ...res.data, url: res.data.url || "", location: res.data.location || "", _dirty: false, _new: false }
              : row
          )
        );
        toast.success("Conference added");
      } else {
        const res = await axios.put(
          `${API}/admin/site/conferences/${it.id}`,
          payload,
          { withCredentials: true }
        );
        setItems((prev) =>
          prev.map((row, i) =>
            i === idx
              ? { ...res.data, url: res.data.url || "", location: res.data.location || "", _dirty: false, _new: false }
              : row
          )
        );
        toast.success("Conference saved");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (idx) => {
    const it = items[idx];
    if (!window.confirm(`Delete "${it.name || "this conference"}"?`)) return;
    if (it._new) {
      setItems((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    setDeletingId(it.id);
    try {
      await axios.delete(`${API}/admin/site/conferences/${it.id}`, {
        withCredentials: true,
      });
      setItems((prev) => prev.filter((_, i) => i !== idx));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const addNew = () =>
    setItems((prev) => [
      ...prev,
      { ...emptyConf(), display_order: (prev[prev.length - 1]?.display_order || 0) + 1 },
    ]);

  return (
    <div className={cardCls} data-testid="admin-conferences-editor">
      <div className="px-6 pt-6 pb-2">
        {sectionHeader({
          Icon: Calendar,
          title: "Conferences Banner",
          subtitle:
            "Industry conferences shown in the deep-green banner strip under the site header. Add a URL if you want the entry to link out in a new tab.",
          action: (
            <button
              type="button"
              onClick={addNew}
              data-testid="admin-conference-add"
              className="inline-flex items-center gap-1.5 bg-brand-olive hover:bg-brand-hover text-brand-cream px-3.5 py-2 rounded-full text-xs font-medium tracking-wide transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add conference
            </button>
          ),
        })}
      </div>

      <div className="px-6 pb-6 space-y-3">
        {loading ? (
          <div className="py-10 text-center text-brand-quiet text-sm">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-brand-quiet text-sm">
            No conferences yet.
          </div>
        ) : (
          items.map((it, idx) => (
            <div
              key={it.id || `new-${idx}`}
              data-testid={`admin-conference-row-${idx}`}
              className={rowFrameCls}
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-4">
                  <label className={labelCls}>Name</label>
                  <input
                    value={it.name}
                    onChange={(e) => patch(idx, { name: e.target.value })}
                    className={inputCls}
                    placeholder="SupplySide Global"
                    data-testid={`admin-conference-name-${idx}`}
                  />
                </div>
                <div className="md:col-span-3">
                  <label className={labelCls}>Dates</label>
                  <input
                    value={it.dates}
                    onChange={(e) => patch(idx, { dates: e.target.value })}
                    className={inputCls}
                    placeholder="Oct 27–30"
                    data-testid={`admin-conference-dates-${idx}`}
                  />
                </div>
                <div className="md:col-span-3">
                  <label className={labelCls}>Location</label>
                  <input
                    value={it.location}
                    onChange={(e) => patch(idx, { location: e.target.value })}
                    className={inputCls}
                    placeholder="Las Vegas"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Order</label>
                  <input
                    type="number"
                    value={it.display_order}
                    onChange={(e) => patch(idx, { display_order: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div className="md:col-span-12">
                  <label className={labelCls}>URL (optional)</label>
                  <input
                    value={it.url}
                    onChange={(e) => patch(idx, { url: e.target.value })}
                    className={inputCls}
                    placeholder="https://www.supplysideglobal.com/"
                    data-testid={`admin-conference-url-${idx}`}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 pt-1">
                <label className="inline-flex items-center gap-2 text-xs text-brand-warm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!it.active}
                    onChange={(e) => patch(idx, { active: e.target.checked })}
                    className="accent-brand-olive"
                    data-testid={`admin-conference-active-${idx}`}
                  />
                  Active on site
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    disabled={deletingId === it.id}
                    className="inline-flex items-center gap-1.5 text-xs text-brand-muted hover:text-red-700 px-2.5 py-1.5 rounded-full border border-transparent hover:border-red-300 transition-colors disabled:opacity-50"
                    data-testid={`admin-conference-delete-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => save(idx)}
                    disabled={!it._dirty || savingId === (it.id || `new-${idx}`)}
                    className="inline-flex items-center gap-1.5 bg-brand-olive hover:bg-brand-hover disabled:opacity-40 text-brand-cream px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors"
                    data-testid={`admin-conference-save-${idx}`}
                  >
                    {savingId === (it.id || `new-${idx}`) ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Save className="w-3.5 h-3.5" />
                    )}
                    Save
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* -------------------- ANNOUNCEMENT EDITOR -------------------- */

function AnnouncementEditor() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/site/announcement`, {
        withCredentials: true,
      });
      setForm({
        enabled: !!res.data.enabled,
        headline: res.data.headline || "",
        body: res.data.body || "",
        primary_label: res.data.primary_label || "Book a meeting",
        primary_url: res.data.primary_url || "",
        contact_email: res.data.contact_email || "agastya@ingredientsignal.com",
        version: res.data.version || "v1",
      });
    } catch {
      toast.error("Couldn't load announcement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.headline.trim() || !form.body.trim()) {
      toast.error("Headline and body are required");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/site/announcement`,
        {
          enabled: !!form.enabled,
          headline: form.headline.trim(),
          body: form.body.trim(),
          primary_label: form.primary_label.trim(),
          primary_url: form.primary_url.trim(),
          contact_email: form.contact_email.trim(),
          version: form.version.trim() || "v1",
        },
        { withCredentials: true }
      );
      toast.success(
        "Announcement saved. Tip: bump the Version to re-show it to visitors who already dismissed the previous one."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className={cardCls}>
        <div className="py-14 text-center text-brand-quiet text-sm">Loading…</div>
      </div>
    );
  }

  return (
    <div className={cardCls} data-testid="admin-announcement-editor">
      <div className="px-6 pt-6 pb-2">
        {sectionHeader({
          Icon: Megaphone,
          title: "First-Visit Announcement",
          subtitle:
            "Shown once per visitor after a 2s delay. Bump the Version to force-re-show it to visitors who already dismissed the previous one (e.g., after switching from SupplySide to the next event).",
          action: null,
        })}
      </div>
      <div className="px-6 pb-6 space-y-4">
        <label className="inline-flex items-center gap-2 text-sm text-brand-warm cursor-pointer">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => change("enabled", e.target.checked)}
            className="accent-brand-olive"
            data-testid="admin-announcement-enabled"
          />
          Enabled on site
        </label>
        <div>
          <label className={labelCls}>Headline</label>
          <input
            value={form.headline}
            onChange={(e) => change("headline", e.target.value)}
            className={inputCls}
            data-testid="admin-announcement-headline"
          />
        </div>
        <div>
          <label className={labelCls}>Body</label>
          <textarea
            rows={3}
            value={form.body}
            onChange={(e) => change("body", e.target.value)}
            className={inputCls}
            data-testid="admin-announcement-body"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Primary button label</label>
            <input
              value={form.primary_label}
              onChange={(e) => change("primary_label", e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Primary button URL</label>
            <input
              value={form.primary_url}
              onChange={(e) => change("primary_url", e.target.value)}
              className={inputCls}
              placeholder="https://…"
            />
          </div>
          <div>
            <label className={labelCls}>Contact email (mailto)</label>
            <input
              value={form.contact_email}
              onChange={(e) => change("contact_email", e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Version tag</label>
            <input
              value={form.version}
              onChange={(e) => change("version", e.target.value)}
              className={inputCls}
              placeholder="supplyside-global-2026"
              data-testid="admin-announcement-version"
            />
          </div>
        </div>
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-brand-quiet max-w-md">
            Changing the Version tag resets the "already dismissed" flag for
            every visitor and shows the popup once again.
          </p>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 bg-brand-olive hover:bg-brand-hover disabled:opacity-40 text-brand-cream px-4 py-2 rounded-full text-sm font-medium transition-colors"
            data-testid="admin-announcement-save"
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- ROOT -------------------- */

export default function SiteContentEditor() {
  return (
    <div className="space-y-8" data-testid="admin-site-content">
      <FeedItemsEditor />
      <ConferencesEditor />
      <AnnouncementEditor />
    </div>
  );
}
